import { useState } from 'react';
import { ArrowUpRight, Check, Loader2 } from 'lucide-react';
import { supabaseAnon } from '../../lib/supabase';

interface NewsletterFormProps {
  variant?: 'light' | 'dark';
}

export default function NewsletterForm({ variant = 'light' }: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabaseAnon
      .from('newsletter_subscribers')
      .insert({ email: email.trim().toLowerCase() });

    if (error) {
      if (error.code === '23505') {
        setAlreadySubscribed(true);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    } else {
      setAlreadySubscribed(false);
      setStatus('success');
    }

    setEmail('');
  };

  if (status === 'success') {
    return (
      <div className={`flex items-center gap-2 text-sm font-bold ${
        variant === 'dark' ? 'text-volt-500' : 'text-night-950'
      }`}>
        <Check size={16} />
        {alreadySubscribed ? 'You are already subscribed.' : 'You are on the list. Welcome!'}
      </div>
    );
  }

  const isDark = variant === 'dark';

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <label htmlFor="newsletter-email" className="sr-only">Email address</label>
        <input
          id="newsletter-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          required
          className={isDark ? 'input-dark flex-1' : 'input-field flex-1'}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-50 ${
            isDark
              ? 'bg-volt-500 text-night-950 hover:bg-volt-400'
              : 'bg-night-950 text-white hover:bg-night-800'
          }`}
        >
          {status === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
        </button>
      </form>
      {status === 'error' && (
        <p className="text-xs text-error-500 mt-2">{errorMsg}</p>
      )}
    </div>
  );
}
